/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * CampaignDetailView — post-launch monitor for a single VoterCampaign.
 *
 * Role wall: CAMPAIGN_MANAGER, MANAGER, ORG_MANAGER, ADMIN.
 * Admin actions (pause / resume / cancel) require MANAGER+; CAMPAIGN_MANAGER
 * can view but cannot mutate.
 *
 * Layout:
 *   1. Role guard.
 *   2. Header: name + status pill + schedule + total recipients + back.
 *   3. Stats grid: sent/delivered/failed/opt-outs/replies + progress bar.
 *   4. Admin actions row (pause / resume / cancel with confirm).
 *   5. Template preview (raw + rendered with sample voter merge).
 *   6. Audience summary (read-only).
 *   7. Recent message log placeholder ("Detailed log coming soon").
 *   8. Error details (only if status === 'failed' and error_message set).
 *   9. Auto-poll every 10s while status is 'sending' or 'scheduled'.
 *
 * The list/detail endpoints aren't all live yet (per api/messaging.ts
 * comments). A 404 on getCampaign is surfaced as a friendly "campaign
 * not found" message rather than a generic error banner.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  ArrowLeft,
  Ban,
  CheckCircle2,
  Loader2,
  Megaphone,
  Pause,
  Play,
  RefreshCw,
} from 'lucide-react';
import {
  cancelCampaign,
  getCampaign,
  pauseCampaign,
  resumeCampaign,
} from '@/api/messaging';
import type { VoterCampaign, VoterCampaignStatus } from '@/types/messaging';
import { ViewHeader } from '@/components/ViewHeader';
import { Skeleton } from '@/components/Skeleton';
import { roleMatches } from '@/lib/roles';

// ---------------------------------------------------------------------------
// Role gates
// ---------------------------------------------------------------------------

const VIEW_ROLES = new Set([
  'SMS_MANAGER',
  'MANAGER',
  'ORG_MANAGER',
  'ADMIN',
]);

const ADMIN_ROLES = new Set(['MANAGER', 'ORG_MANAGER', 'ADMIN']);

function canView(userRole: string | undefined): boolean {
  if (!userRole) return false;
  return roleMatches(userRole, VIEW_ROLES);
}

function canMutate(userRole: string | undefined): boolean {
  if (!userRole) return false;
  return roleMatches(userRole, ADMIN_ROLES);
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function statusBadge(s: VoterCampaignStatus | string): {
  label: string;
  cls: string;
} {
  switch (s) {
    case 'draft':
      return {
        label: 'Draft',
        cls: 'bg-bg-elevated text-fg-secondary border-border-base',
      };
    case 'scheduled':
      return {
        label: 'Scheduled',
        cls: 'bg-status-warning-bg/30 text-status-warning border-status-warning-bg/50',
      };
    case 'sending':
      return {
        label: 'Sending',
        cls: 'bg-status-info-bg/30 text-status-info border-status-info-bg/50',
      };
    case 'completed':
      return {
        label: 'Completed',
        cls: 'bg-status-success-bg/30 text-status-success border-status-success-bg/50',
      };
    case 'paused':
      return {
        label: 'Paused',
        cls: 'bg-status-warning-bg/30 text-status-warning border-status-warning-bg/50',
      };
    case 'failed':
      return {
        label: 'Failed',
        cls: 'bg-status-danger-bg/30 text-status-danger border-status-danger-bg/50',
      };
    case 'cancelled':
      return {
        label: 'Cancelled',
        cls: 'bg-status-danger-bg/30 text-status-danger border-status-danger-bg/50',
      };
    default:
      return {
        label: String(s),
        cls: 'bg-bg-elevated text-fg-secondary border-border-base',
      };
  }
}

function isTerminal(s: VoterCampaignStatus | undefined): boolean {
  return s === 'completed' || s === 'cancelled' || s === 'failed';
}

function renderTemplate(tpl: string): string {
  return tpl
    .replace(/\{first_name\}/g, 'Jane')
    .replace(/\{last_name\}/g, 'Voter')
    .replace(/\{org_name\}/g, 'Your Organization')
    .replace(/\{volunteer_first_name\}/g, 'Sam');
}

function pct(num: number, denom: number): string {
  if (!denom) return '—';
  return `${Math.round((num / denom) * 100)}%`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface CampaignDetailViewProps {
  campaignId: number;
  userRole?: string;
  onBack?: () => void;
}

export function CampaignDetailView({
  campaignId,
  userRole,
  onBack,
}: CampaignDetailViewProps) {
  if (!canView(userRole)) {
    return (
      <div className="min-h-dvh bg-bg-base text-fg-primary p-6">
        <div className="max-w-2xl mx-auto rounded-lg border border-status-danger-bg/30 bg-status-danger-bg/20 p-6">
          <h2 className="text-lg font-semibold text-status-danger mb-2">
            Access Denied
          </h2>
          <p className="text-fg-secondary">
            Viewing campaigns is restricted to campaign managers and above.
          </p>
        </div>
      </div>
    );
  }

  const [campaign, setCampaign] = useState<VoterCampaign | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [reloadCounter, setReloadCounter] = useState(0);

  const [actionBusy, setActionBusy] = useState<
    'pause' | 'resume' | 'cancel' | null
  >(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const [confirmCancel, setConfirmCancel] = useState(false);

  // -------------------------------------------------------------------------
  // Fetch campaign
  // -------------------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setNotFound(false);

    getCampaign(campaignId)
      .then((c) => {
        if (cancelled) return;
        setCampaign(c);
        setLoading(false);
      })
      .catch((e: any) => {
        if (cancelled) return;
        const status = e?.response?.status;
        if (status === 404) {
          setNotFound(true);
        } else if (status === 403) {
          setError('Your role does not have access to this campaign.');
        } else if (status) {
          setError(`Backend error ${status} loading campaign.`);
        } else {
          setError('Could not reach the messaging backend.');
        }
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [campaignId, reloadCounter]);

  // -------------------------------------------------------------------------
  // Auto-refresh every 10s while in a live state.
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!campaign) return;
    if (isTerminal(campaign.status)) return;
    if (campaign.status !== 'sending' && campaign.status !== 'scheduled') return;
    const id = window.setInterval(() => {
      setReloadCounter((n) => n + 1);
    }, 10_000);
    return () => window.clearInterval(id);
  }, [campaign]);

  // -------------------------------------------------------------------------
  // Action handlers
  // -------------------------------------------------------------------------
  const handlePause = useCallback(async () => {
    setActionBusy('pause');
    setActionError(null);
    try {
      const next = await pauseCampaign(campaignId);
      setCampaign(next);
    } catch (e: any) {
      const status = e?.response?.status;
      const detail =
        e?.response?.data?.detail || e?.message || 'Could not pause.';
      setActionError(status ? `Error ${status}: ${detail}` : detail);
    } finally {
      setActionBusy(null);
    }
  }, [campaignId]);

  const handleResume = useCallback(async () => {
    setActionBusy('resume');
    setActionError(null);
    try {
      const next = await resumeCampaign(campaignId);
      setCampaign(next);
    } catch (e: any) {
      const status = e?.response?.status;
      const detail =
        e?.response?.data?.detail || e?.message || 'Could not resume.';
      setActionError(status ? `Error ${status}: ${detail}` : detail);
    } finally {
      setActionBusy(null);
    }
  }, [campaignId]);

  const handleCancel = useCallback(async () => {
    setActionBusy('cancel');
    setActionError(null);
    try {
      const next = await cancelCampaign(campaignId);
      setCampaign(next);
      setConfirmCancel(false);
    } catch (e: any) {
      const status = e?.response?.status;
      const detail =
        e?.response?.data?.detail || e?.message || 'Could not cancel.';
      setActionError(status ? `Error ${status}: ${detail}` : detail);
    } finally {
      setActionBusy(null);
    }
  }, [campaignId]);

  // -------------------------------------------------------------------------
  // Derived
  // -------------------------------------------------------------------------
  const showPause = useMemo(() => {
    if (!campaign) return false;
    return campaign.status === 'sending' && campaign.paused_at == null;
  }, [campaign]);

  const showResume = useMemo(() => {
    if (!campaign) return false;
    return campaign.paused_at != null;
  }, [campaign]);

  const showCancel = useMemo(() => {
    if (!campaign) return false;
    return (
      campaign.status === 'scheduled' ||
      campaign.status === 'sending' ||
      !!campaign.paused_at
    );
  }, [campaign]);

  const sendProgress = useMemo(() => {
    if (!campaign || !campaign.total_recipients) return 0;
    return Math.min(
      100,
      Math.round((campaign.sent_count / campaign.total_recipients) * 100),
    );
  }, [campaign]);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  if (loading && !campaign) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-16" />
        <Skeleton className="h-24" />
        <Skeleton className="h-48" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="space-y-4">
        <ViewHeader
          title={
            <span className="flex items-center gap-2">
              <Megaphone className="w-6 h-6 text-fg-muted" />
              Campaign not found
            </span>
          }
          tagline={`Campaign #${campaignId} doesn't exist or hasn't synced yet.`}
          actions={
            onBack && (
              <button
                type="button"
                onClick={onBack}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded bg-bg-elevated hover:bg-border-subtle text-fg-primary text-sm font-semibold"
              >
                <ArrowLeft className="w-4 h-4" aria-hidden="true" />
                Back
              </button>
            )
          }
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <div className="bg-status-danger-bg/30 rounded-xl p-4 border border-status-danger-bg text-status-danger text-sm flex items-center justify-between gap-3">
          <span>{error}</span>
          <button
            onClick={() => setReloadCounter((n) => n + 1)}
            className="bg-accent hover:bg-accent-hover text-white px-3 py-1.5 rounded text-xs font-bold"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!campaign) return null;

  const sbadge = statusBadge(
    campaign.paused_at && campaign.status === 'sending'
      ? 'paused'
      : campaign.status,
  );

  return (
    <div className="space-y-6">
      <ViewHeader
        title={
          <span className="flex items-center gap-2">
            <Megaphone className="w-6 h-6 text-accent" />
            {campaign.name}
          </span>
        }
        tagline={
          <span className="inline-flex items-center gap-3 flex-wrap">
            <span
              className={`inline-block px-2 py-0.5 rounded border text-[11px] font-bold uppercase tracking-wide ${sbadge.cls}`}
            >
              {sbadge.label}
            </span>
            <span className="text-fg-muted">
              {campaign.scheduled_at
                ? `Scheduled ${formatDateTime(campaign.scheduled_at)}`
                : campaign.started_at
                  ? `Started ${formatDateTime(campaign.started_at)}`
                  : `Created ${formatDateTime(campaign.created_at)}`}
            </span>
            <span className="text-fg-muted">
              · {campaign.total_recipients.toLocaleString()} recipients
            </span>
            {!isTerminal(campaign.status) && (
              <span className="inline-flex items-center gap-1 text-fg-muted text-xs">
                <RefreshCw
                  className="w-3 h-3 animate-spin"
                  aria-hidden="true"
                />
                Live · auto-refresh every 10s
              </span>
            )}
          </span>
        }
        actions={
          onBack && (
            <button
              type="button"
              onClick={onBack}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded bg-bg-elevated hover:bg-border-subtle text-fg-primary text-sm font-semibold"
            >
              <ArrowLeft className="w-4 h-4" aria-hidden="true" />
              Back
            </button>
          )
        }
      />

      {/* Stats grid */}
      <div className="bg-bg-raised rounded-xl p-6 border border-border-base space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <StatCard
            label="Sent"
            value={`${campaign.sent_count.toLocaleString()} / ${campaign.total_recipients.toLocaleString()}`}
            tone="info"
          />
          <StatCard
            label="Delivered"
            value={`${campaign.delivered_count.toLocaleString()} · ${pct(campaign.delivered_count, campaign.sent_count)}`}
            tone="success"
          />
          <StatCard
            label="Failed"
            value={campaign.failed_count.toLocaleString()}
            tone="danger"
          />
          <StatCard
            label="Opt-outs received"
            value={campaign.opt_out_count.toLocaleString()}
            tone="warning"
          />
          <StatCard
            label="Replies"
            value={campaign.reply_count.toLocaleString()}
            tone="neutral"
          />
        </div>

        {/* Progress bar */}
        <div>
          <div className="flex items-center justify-between text-xs text-fg-muted mb-1">
            <span>Send progress</span>
            <span className="tabular-nums">{sendProgress}%</span>
          </div>
          <div className="h-2 rounded-full bg-bg-elevated overflow-hidden">
            <div
              className="h-full bg-status-info transition-all"
              style={{ width: `${sendProgress}%` }}
              aria-valuenow={sendProgress}
              aria-valuemin={0}
              aria-valuemax={100}
              role="progressbar"
            />
          </div>
        </div>
      </div>

      {/* Admin actions */}
      {canMutate(userRole) && (showPause || showResume || showCancel) && (
        <div className="bg-bg-raised rounded-xl p-4 border border-border-base">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="text-sm text-fg-secondary">
              <span className="text-xs uppercase tracking-wide text-fg-muted font-semibold mr-2">
                Admin actions
              </span>
              {actionBusy && (
                <span className="inline-flex items-center gap-1.5 text-fg-muted">
                  <Loader2
                    className="w-3.5 h-3.5 animate-spin"
                    aria-hidden="true"
                  />
                  Working…
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {showPause && (
                <button
                  type="button"
                  onClick={handlePause}
                  disabled={!!actionBusy}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded bg-status-warning-bg hover:opacity-90 disabled:opacity-40 text-white text-sm font-semibold"
                >
                  <Pause className="w-4 h-4" aria-hidden="true" />
                  Pause
                </button>
              )}
              {showResume && (
                <button
                  type="button"
                  onClick={handleResume}
                  disabled={!!actionBusy}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded bg-status-success-bg hover:opacity-90 disabled:opacity-40 text-white text-sm font-semibold"
                >
                  <Play className="w-4 h-4" aria-hidden="true" />
                  Resume
                </button>
              )}
              {showCancel && (
                <button
                  type="button"
                  onClick={() => setConfirmCancel(true)}
                  disabled={!!actionBusy}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded bg-status-danger-bg hover:opacity-90 disabled:opacity-40 text-white text-sm font-semibold"
                >
                  <Ban className="w-4 h-4" aria-hidden="true" />
                  Cancel
                </button>
              )}
            </div>
          </div>

          {actionError && (
            <div className="mt-3 rounded p-2 border border-status-danger-bg bg-status-danger-bg/30 text-status-danger text-xs">
              {actionError}
            </div>
          )}
        </div>
      )}

      {/* Cancel confirm dialog */}
      {confirmCancel && (
        <ConfirmCancelDialog
          onConfirm={handleCancel}
          onClose={() => setConfirmCancel(false)}
          busy={actionBusy === 'cancel'}
        />
      )}

      {/* Failure error panel */}
      {campaign.status === 'failed' && campaign.error_message && (
        <div className="bg-status-danger-bg/30 rounded-xl p-4 border border-status-danger-bg">
          <div className="flex items-start gap-2">
            <AlertTriangle
              className="w-5 h-5 text-status-danger flex-shrink-0 mt-0.5"
              aria-hidden="true"
            />
            <div>
              <h3 className="text-sm font-bold text-status-danger mb-1">
                Campaign failed
              </h3>
              <p className="text-sm text-status-danger whitespace-pre-wrap">
                {campaign.error_message}
              </p>
              {campaign.last_processed_at && (
                <p className="text-xs text-status-danger/70 mt-2">
                  Last processed: {formatDateTime(campaign.last_processed_at)}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Template preview */}
      <Section title="Template">
        <div className="space-y-3">
          <div>
            <div className="text-[10px] uppercase tracking-wide text-fg-muted font-semibold mb-1">
              Raw template
            </div>
            <pre className="bg-bg-base border border-border-base rounded p-3 text-xs text-fg-secondary font-mono whitespace-pre-wrap break-words">
              {campaign.template}
            </pre>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wide text-fg-muted font-semibold mb-1">
              Rendered example (Jane Voter)
            </div>
            <div className="bg-bg-base border border-border-base rounded p-3 text-sm text-fg-primary">
              {renderTemplate(campaign.template)}
            </div>
          </div>
        </div>
      </Section>

      {/* Audience summary placeholder. Backend doesn't yet return the
          AudienceQuery on VoterCampaign, so we render a minimal note here.
          The composer is the source of truth for what was sent. */}
      <Section title="Audience">
        <div className="text-sm text-fg-secondary">
          {campaign.total_recipients.toLocaleString()} recipients were
          enrolled when this campaign launched. The audience filter used at
          launch (precincts, party score, etc.) isn&apos;t exposed on the
          campaign record yet — open the original campaign event in the
          consent ledger to see the full query.
        </div>
      </Section>

      {/* Recent message log placeholder. We don't have a backing endpoint
          for "messages by campaign" yet, so show a friendly placeholder. */}
      <Section title="Recent message log">
        <div className="text-sm text-fg-muted">
          Detailed message-level log coming soon. For now, use the per-voter
          conversation thread to inspect individual sends.
        </div>
      </Section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-bg-raised rounded-xl p-6 border border-border-base space-y-3">
      <h2 className="text-lg font-bold text-fg-primary">{title}</h2>
      {children}
    </div>
  );
}

function StatCard({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone: 'success' | 'danger' | 'warning' | 'info' | 'neutral';
}) {
  const palette: Record<string, { bg: string; border: string; text: string }> =
    {
      success: {
        bg: 'bg-status-success-bg/25',
        border: 'border-status-success-bg/50',
        text: 'text-status-success',
      },
      danger: {
        bg: 'bg-status-danger-bg/25',
        border: 'border-status-danger-bg/50',
        text: 'text-status-danger',
      },
      warning: {
        bg: 'bg-status-warning-bg/25',
        border: 'border-status-warning-bg/50',
        text: 'text-status-warning',
      },
      info: {
        bg: 'bg-status-info-bg/25',
        border: 'border-status-info-bg/50',
        text: 'text-status-info',
      },
      neutral: {
        bg: 'bg-bg-base/40',
        border: 'border-border-base',
        text: 'text-fg-secondary',
      },
    };
  const p = palette[tone];
  return (
    <div className={`rounded-lg border px-3 py-3 ${p.bg} ${p.border}`}>
      <div className={`text-[10px] uppercase tracking-wider ${p.text}`}>
        {label}
      </div>
      <div className="mt-0.5 text-2xl font-bold text-fg-primary tabular-nums">
        {value}
      </div>
    </div>
  );
}

function ConfirmCancelDialog({
  onConfirm,
  onClose,
  busy,
}: {
  onConfirm: () => void;
  onClose: () => void;
  busy: boolean;
}) {
  return (
    <>
      <div
        onClick={onClose}
        className="fixed inset-0 bg-black/50 z-40"
        aria-hidden="true"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Confirm cancel campaign"
        className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md bg-bg-elevated border border-border-base rounded-lg shadow-2xl z-50 p-5 space-y-4"
      >
        <div className="flex items-start gap-3">
          <Ban
            className="w-6 h-6 text-status-danger flex-shrink-0"
            aria-hidden="true"
          />
          <div>
            <h2 className="text-lg font-bold text-fg-primary">
              Cancel campaign?
            </h2>
            <p className="text-sm text-fg-secondary mt-1">
              Cancelling is terminal — the campaign cannot be resumed. Any
              messages already sent will remain delivered; queued messages
              will be dropped.
            </p>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="px-3 py-2 rounded bg-bg-base hover:bg-border-subtle text-fg-primary text-sm font-semibold"
          >
            Keep running
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded bg-status-danger-bg hover:opacity-90 disabled:opacity-40 text-white text-sm font-bold"
          >
            {busy ? (
              <>
                <Loader2
                  className="w-4 h-4 animate-spin"
                  aria-hidden="true"
                />
                Cancelling…
              </>
            ) : (
              <>
                <CheckCircle2 className="w-4 h-4" aria-hidden="true" />
                Yes, cancel
              </>
            )}
          </button>
        </div>
      </div>
    </>
  );
}

export default CampaignDetailView;
